import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { acquistiApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Search, Loader2, Package, CheckSquare, X, Tags, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';
import AcquistiHeader from '../../components/acquisti/AcquistiHeader';
import PrezzarioItem from '../../components/acquisti/PrezzarioItem';
import ProposteAiDialog from '../../components/acquisti/ProposteAiDialog';
import CategoriaSelect, { NESSUNA } from '../../components/acquisti/CategoriaSelect';
import { useAuth } from '../../context/AuthContext';

const AcquistiPrezzarioPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.ruolo === 'admin' || user?.is_super_admin;
  const [stato, setStato] = useState(null);
  const [fornitori, setFornitori] = useState([]);
  const [q, setQ] = useState('');
  const [fornitoreId, setFornitoreId] = useState('tutti');
  const [soloDaClassificare, setSoloDaClassificare] = useState(false);
  const [risultati, setRisultati] = useState([]);
  const [cercando, setCercando] = useState(false);
  const [selezione, setSelezione] = useState(null); // null = modalità disattiva
  const [categoriaBulk, setCategoriaBulk] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [proposteAperte, setProposteAperte] = useState(false);
  const debounce = useRef(null);

  const ricaricaStato = useCallback(() => {
    acquistiApi.getStato().then(setStato).catch(() => {});
  }, []);

  useEffect(() => {
    ricaricaStato();
    acquistiApi.getFornitori().then(setFornitori).catch(() => {});
  }, [ricaricaStato]);

  const cerca = useCallback(async () => {
    setCercando(true);
    try {
      setRisultati(await acquistiApi.getPrezzario(
        q.trim(),
        fornitoreId === 'tutti' ? '' : fornitoreId,
        soloDaClassificare ? 200 : 40,
        soloDaClassificare
      ));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella ricerca');
    } finally {
      setCercando(false);
    }
  }, [q, fornitoreId, soloDaClassificare]);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(cerca, 250);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [cerca]);

  const salvaCategoria = async (p, valore) => {
    const categoria = valore === NESSUNA ? null : valore;
    setSalvando(true);
    try {
      await acquistiApi.setCategoriaProdotto(p.id, categoria);
      setRisultati((prec) => prec.map((x) => (
        x.id === p.id ? { ...x, categoria, categoria_effettiva: categoria } : x
      )));
      ricaricaStato();
      toast.success(categoria
        ? `${p.descrizione.slice(0, 30)} → ${categoria}`
        : 'Categoria rimossa');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Salvataggio non riuscito');
    } finally {
      setSalvando(false);
    }
  };

  const toggleSel = (id) => setSelezione((prec) => {
    const nuova = new Set(prec || []);
    if (nuova.has(id)) nuova.delete(id); else nuova.add(id);
    return nuova;
  });

  const applicaMultipla = async () => {
    const ids = Array.from(selezione || []);
    const categoria = categoriaBulk === NESSUNA ? null : categoriaBulk;
    setSalvando(true);
    try {
      const res = await acquistiApi.setCategoriaProdottiMultipli(ids, categoria);
      toast.success(`${res.prodotti_aggiornati} prodotti classificati come ${categoria || 'DA CLASSIFICARE'}`);
      setSelezione(null);
      setCategoriaBulk('');
      ricaricaStato();
      await cerca();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Classificazione non riuscita');
    } finally {
      setSalvando(false);
    }
  };

  const nSelezionati = selezione ? selezione.size : 0;
  const nDaClassificare = stato?.n_prodotti_da_classificare || 0;
  const rimastiInElenco = risultati.filter((p) => !(p.categoria_effettiva || p.categoria)).length;

  return (
    <div className={cn('min-h-screen', selezione ? 'pb-48' : 'pb-24')}>
      <AcquistiHeader
        titolo="Prezzario"
        sottotitolo="Quanto stai pagando ogni prodotto"
        azione={isAdmin && !stato?.vuoto && (
          <Button
            size="sm"
            variant={selezione ? 'default' : 'outline'}
            className="shrink-0 h-9 text-xs"
            onClick={() => { setSelezione(selezione ? null : new Set()); setCategoriaBulk(''); }}
            data-testid="prezzario-selezione-toggle"
          >
            {selezione ? <X className="w-4 h-4 mr-1" /> : <CheckSquare className="w-4 h-4 mr-1" />}
            {selezione ? 'Annulla' : 'Seleziona'}
          </Button>
        )}
      />

      <main className="px-4 py-4 max-w-lg mx-auto space-y-4">
        {stato && !stato.vuoto && (
          <div className="grid grid-cols-3 gap-2" data-testid="prezzario-kpi">
            {[
              { label: 'Prodotti', valore: stato.n_prodotti },
              { label: 'Fornitori', valore: stato.n_fornitori },
              { label: 'Fatture', valore: stato.n_documenti },
            ].map((k) => (
              <Card key={k.label}>
                <CardContent className="p-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{k.label}</p>
                  <p className="text-lg font-bold tabular-nums">{k.valore}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="space-y-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca: giardiniera, mozzarella, birra…"
              className="h-12 pl-9"
              data-testid="prezzario-search-input"
            />
            {cercando && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />}
          </div>
          <Select value={fornitoreId} onValueChange={setFornitoreId}>
            <SelectTrigger className="h-10" data-testid="prezzario-fornitore-select">
              <SelectValue placeholder="Tutti i fornitori" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti i fornitori</SelectItem>
              {fornitori.map((f) => (
                <SelectItem key={f.id} value={f.id}>{f.denominazione}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {!stato?.vuoto && (
            <div className="flex gap-2">
              {[
                { id: 'tutti', label: 'Tutti i prodotti', attivo: !soloDaClassificare },
                { id: 'da-classificare', label: `Da classificare${nDaClassificare ? ` (${nDaClassificare})` : ''}`, attivo: soloDaClassificare },
              ].map((c) => (
                <button
                  key={c.id}
                  onClick={() => { setSoloDaClassificare(c.id === 'da-classificare'); setSelezione(null); }}
                  data-testid={`prezzario-filtro-${c.id}`}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide transition-colors',
                    c.attivo
                      ? c.id === 'da-classificare'
                        ? 'bg-amber-500 text-white'
                        : 'bg-primary text-primary-foreground'
                      : 'bg-secondary text-muted-foreground hover:text-foreground'
                  )}
                >
                  {c.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {stato?.vuoto && (
          <Card data-testid="prezzario-vuoto">
            <CardContent className="p-4 text-sm text-muted-foreground space-y-2">
              <p>Nessuna fattura di acquisto in archivio.</p>
              <p>Vai su <strong>Carica</strong> e importa gli XML o lo ZIP scaricato dal Digital Hub.</p>
            </CardContent>
          </Card>
        )}

        {!stato?.vuoto && (
          <>
            <p className="text-xs text-muted-foreground" data-testid="prezzario-conteggio">
              {soloDaClassificare
                ? rimastiInElenco
                  ? `${rimastiInElenco} prodotti da classificare, dal più costoso`
                  : 'Tutti classificati: aggiorna l’elenco per vedere i prossimi'
                : q.trim() ? `${risultati.length} prodotti trovati` : 'Ultimi prodotti acquistati'}
              {selezione && ' · tocca le righe per selezionarle'}
              {soloDaClassificare && !selezione && (
                <button
                  onClick={cerca}
                  className="ml-2 underline font-semibold"
                  data-testid="prezzario-aggiorna-elenco"
                >
                  Aggiorna
                </button>
              )}
            </p>

            {soloDaClassificare && isAdmin && nDaClassificare > 0 && !selezione && (
              <Button
                variant="outline"
                className="w-full border-primary/40"
                onClick={() => setProposteAperte(true)}
                data-testid="prezzario-proponi-ai"
              >
                <Sparkles className="w-4 h-4 mr-2 text-primary" />
                Proponi le categorie con l’AI
              </Button>
            )}
            <div className="space-y-2" data-testid="prezzario-risultati">
              {risultati.map((p) => (
                <PrezzarioItem
                  key={p.id}
                  p={p}
                  categorie={stato?.categorie || []}
                  isAdmin={isAdmin}
                  selezione={!!selezione}
                  selezionato={!!selezione?.has(p.id)}
                  salvando={salvando}
                  onToggle={() => toggleSel(p.id)}
                  onApri={() => navigate(`/acquisti/prodotti/${p.id}`)}
                  onCategoria={salvaCategoria}
                />
              ))}
              {!cercando && risultati.length === 0 && (
                <Card data-testid="prezzario-nessun-risultato">
                  <CardContent className="p-4 text-sm text-muted-foreground flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    {soloDaClassificare
                      ? 'Tutti i prodotti hanno una categoria.'
                      : `Nessun prodotto trovato per "${q}".`}
                  </CardContent>
                </Card>
              )}
            </div>

            <p className="text-[11px] text-muted-foreground leading-relaxed">
              I prezzi provengono dalle righe delle fatture e vengono confrontati solo a parità di
              unità di misura. Le note di credito non modificano i prezzi. La categoria scelta qui
              viene ricordata per i prossimi import dello stesso prodotto.
            </p>
          </>
        )}
      </main>

      {selezione && (
        <div
          className="fixed left-0 right-0 z-40 border-t border-border bg-white shadow-[0_-6px_20px_rgba(0,0,0,0.08)]"          style={{ bottom: 'calc(4rem + env(safe-area-inset-bottom))' }}
          data-testid="prezzario-barra-multipla"
        >
          <div className="max-w-lg mx-auto px-4 py-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold" data-testid="prezzario-n-selezionati">
                {nSelezionati} selezionati
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 text-xs"
                  onClick={() => setSelezione(new Set(risultati.map((p) => p.id)))}
                  data-testid="prezzario-seleziona-tutti"
                >
                  Seleziona tutti
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 text-xs"
                  onClick={() => setSelezione(new Set())}
                  disabled={!nSelezionati}
                  data-testid="prezzario-deseleziona"
                >
                  Azzera
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <CategoriaSelect
                valore={categoriaBulk}
                categorie={stato?.categorie || []}
                onChange={setCategoriaBulk}
                placeholder="Scegli categoria"
                testid="prezzario-categoria-bulk"
                className="h-10 flex-1"
              />
              <Button
                className="h-10 shrink-0"
                disabled={!nSelezionati || !categoriaBulk || salvando}
                onClick={applicaMultipla}
                data-testid="prezzario-applica-multipla"
              >
                {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tags className="w-4 h-4 mr-1" />}
                Applica
              </Button>
            </div>
          </div>
        </div>
      )}
      <ProposteAiDialog
        aperto={proposteAperte}
        onChiudi={() => setProposteAperte(false)}
        categorie={stato?.categorie || []}
        onApplicato={() => { ricaricaStato(); cerca(); }}
      />
    </div>
  );
};

export default AcquistiPrezzarioPage;
